import { Box, Card, Flex, Group, Skeleton, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Fragment } from 'react'

import { useCatchSummary } from '@/api/catch/use-catch-summary'
import { Typography } from '@/components/atoms/Typography'

type TCatchSummaryProps = {
  sessionIds: string[]
  compact?: boolean
}

const StatValue = ({
  label,
  value,
  unit,
  compact,
}: {
  label: string
  value: number
  unit: string
  compact?: boolean
}) => (
  <Flex gap={4}>
    <Typography size={compact ? 'sm' : 'md'} fw={200}>
      {label}:
    </Typography>
    <Typography size={compact ? 'sm' : 'md'} fw={700}>
      {value} {unit}
    </Typography>
  </Flex>
)

const FishTypeStats = ({ label, value }: { label: string; value: number }) => (
  <Fragment>
    <Text size="xs" c="dimmed" fw={300} component="span">
      {label}:
    </Text>
    <Text size="xs" c="black" fw={500}>
      {value}
    </Text>
  </Fragment>
)

export const CatchSummary: React.FC<TCatchSummaryProps> = ({ sessionIds, compact = false }) => {
  const t = useTranslations('catch')
  const { data, isLoading } = useCatchSummary(sessionIds)

  if (isLoading) {
    return <Skeleton height={130} width="100%" p="xl" />
  }

  if (!data) {
    return null
  }

  const hasWeight = data.weight.total > 0
  const hasLength = data.length.total > 0

  return (
    <Card withBorder p={compact ? 'xs' : 'md'} w="100%">
      <Stack gap={compact ? 4 : 'sm'}>
        <Group justify="space-between" align="center">
          <Typography fw={700} size={compact ? 'sm' : 'md'}>
            {t('summary')}
          </Typography>
          <StatValue label={t('total_catches')} value={data.totalCatches} unit="" compact={compact} />
        </Group>

        {hasWeight && (
          <Group justify="space-between" align="center">
            <StatValue label={t('total_weight')} value={data.weight.total} unit="kg" compact={compact} />
            <StatValue label={t('average_weight')} value={data.weight.average} unit="kg" compact={compact} />
          </Group>
        )}

        {hasLength && (
          <Group justify="space-between" align="center">
            <StatValue label={t('total_length')} value={data.length.total} unit="cm" compact={compact} />
            <StatValue label={t('average_length')} value={data.length.average} unit="cm" compact={compact} />
          </Group>
        )}

        {Object.entries(data.byFishType).length > 0 && (
          <Box>
            <Flex gap={4} wrap="wrap">
              {Object.entries(data.byFishType).map(([fishName, count], index, array) => {
                const weight = data.weight.byFishType[fishName]
                const length = data.length.byFishType[fishName]
                const avgWeight = data.weight.averageByFishType[fishName]
                const avgLength = data.length.averageByFishType[fishName]
                return (
                  <Fragment key={fishName}>
                    <FishTypeStats label={fishName} value={count} />
                    {(weight > 0 || length > 0) && (
                      <>
                        <Text size="xs" c="dimmed" fw={300} component="span">
                          (
                        </Text>
                        {weight > 0 && (
                          <Text size="xs" c="dimmed" fw={300} component="span">
                            {weight}kg{avgWeight ? ` (∅ ${avgWeight}kg)` : ''}
                          </Text>
                        )}
                        {weight > 0 && length > 0 && (
                          <Text size="xs" c="dimmed" fw={300} component="span">
                            ,
                          </Text>
                        )}
                        {length > 0 && (
                          <Text size="xs" c="dimmed" fw={300} component="span">
                            {length}cm{avgLength ? ` (∅ ${avgLength}cm)` : ''}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed" fw={300} component="span">
                          )
                        </Text>
                      </>
                    )}
                    {index < array.length - 1 && (
                      <Text size="xs" c="black" fw={300}>
                        |
                      </Text>
                    )}
                  </Fragment>
                )
              })}
            </Flex>
          </Box>
        )}
      </Stack>
    </Card>
  )
}
